import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { IMAGES } from "@/constants/assets";
import { cn } from "@/lib/utils";

const links = [
  { to: "/", label: "Home", id: NAV.home },
  { to: "/about", label: "About Us", id: NAV.about },
  { to: "/services", label: "Services", id: NAV.services },
  { to: "/quote", label: "Get Quote", id: NAV.quote },
  { to: "/careers", label: "Careers", id: NAV.careers },
  { to: "/reviews", label: "Reviews", id: NAV.reviews },
  { to: "/contact", label: "Contact Us", id: NAV.contact },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar */}
      <div className="bg-black text-slate-300 text-xs sm:text-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-600 red-pulse" />
            <span className="font-condensed text-[11px] font-bold tracking-[0.28em] uppercase text-white">
              WA Statewide · Security Services
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a href="tel:2067429195" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span className="font-medium">206-742-9195</span>
            </a>
            <a href="mailto:info@marcopolosecurity.com" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5" />
              <span className="font-medium">info@marcopolosecurity.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-black/90 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
            <div className="relative w-11 h-11 rounded-sm bg-white/5 border border-white/10 p-1.5">
              <img
                src={IMAGES.logo}
                alt="Marco Polo Security logo"
                className="w-full h-full object-contain invert-logo"
              />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 border-2 border-black" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-black text-white text-[15px] tracking-tight uppercase">Marco Polo</span>
              <span className="font-condensed text-[10px] tracking-[0.22em] uppercase text-red-500 font-semibold">Security · WA</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.id}
                className={({ isActive }) =>
                  cn(
                    "px-3.5 py-2 rounded text-sm font-condensed uppercase tracking-wider font-semibold transition-colors",
                    isActive
                      ? "text-white bg-white/10"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/quote"
              data-testid="header-get-quote-btn"
              className="bg-white hover:bg-slate-100 text-black px-4 py-2 rounded font-condensed uppercase tracking-wider text-sm font-bold transition-colors relative"
            >
              Get Quote
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-600 rounded-full red-pulse" />
            </Link>
          </div>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-slate-300 hover:text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/8 bg-black">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.id}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "px-3 py-2.5 rounded text-sm font-condensed uppercase tracking-wider font-semibold",
                      isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5"
                    )
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                onClick={() => setOpen(false)}
                className="mt-2 bg-white text-black text-center px-4 py-2.5 rounded text-sm font-bold uppercase font-condensed tracking-wider"
              >
                Get Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
